import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../lib/utils';
import { Tooltip } from '../Tooltip';

import type { Props } from './types';

// ── Variants ──────────────────────────────────────────────────────────────────

/**
 * A banner's colour says what kind of thing it is announcing rather than how
 * urgent it is, which is what an alert's five degrees are for. A member's tags
 * and standing notices are told apart at a glance by shade, so they keep one
 * each.
 */
const bannerVariants = tv({
  slots: {
    root: 'm-1 flex overflow-hidden rounded-lg',
    rail: 'w-1 shrink-0',
    body: 'flex min-w-0 flex-1 items-center gap-1.5 px-2 py-2 text-xs'
  },
  variants: {
    color: {
      gray: {
        rail: 'bg-uniform-gray-500',
        body: 'bg-uniform-gray-100 text-uniform-gray-700 dark:bg-uniform-gray-800 dark:text-uniform-gray-300'
      },
      red: {
        rail: 'bg-uniform-red-600',
        body: 'bg-uniform-red-100 text-uniform-red-800 dark:bg-uniform-red-900 dark:text-uniform-red-200'
      },
      orange: {
        rail: 'bg-orange-600',
        body: 'bg-orange-100 text-orange-900 dark:bg-orange-900 dark:text-orange-200'
      },
      amber: {
        rail: 'bg-amber-600',
        body: 'bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-200'
      },
      yellow: {
        rail: 'bg-yellow-500',
        body: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-900 dark:text-yellow-200'
      },
      lime: {
        rail: 'bg-lime-600',
        body: 'bg-lime-100 text-lime-900 dark:bg-lime-900 dark:text-lime-200'
      },
      green: {
        rail: 'bg-green-600',
        body: 'bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-200'
      },
      blue: {
        rail: 'bg-blue-600',
        body: 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-200'
      },
      indigo: {
        rail: 'bg-indigo-600',
        body: 'bg-indigo-100 text-indigo-900 dark:bg-indigo-900 dark:text-indigo-200'
      }
    }
  },
  defaultVariants: { color: 'gray' }
});

type BannerVariants = VariantProps<typeof bannerVariants>;

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * A line of standing news about whoever is on screen — a tag, a birthday, a
 * missing signature — stacked with its fellows above the page.
 *
 * The row carries the label alone, held to one line and cut off where it runs
 * out of room, so a column of banners costs the same space whatever they say.
 * Keep a label short; what else there is to say belongs in the detail, which
 * hovering reads out.
 */
function Banner({ children, className, color, detail, icon, label }: Props) {
  const styles = bannerVariants({ color });

  return (
    <div className={cn(styles.root(), className)}>
      <div className={styles.rail()} />

      <div className={styles.body()}>
        {icon && <FontAwesomeIcon className="shrink-0" icon={icon as IconProp} />}

        {detail ? (
          /*
           * The detail is only in the bubble, so the label is the way to it and
           * has to take focus. A banner without one is all on the page already,
           * which keeps a linked banner to the one stop its link gives it.
           */
          <Tooltip content={detail} focusable triggerClassName="min-w-0 flex-1">
            <strong className="block truncate">{label}</strong>
          </Tooltip>
        ) : (
          <strong className="min-w-0 flex-1 truncate">{label}</strong>
        )}

        {children}
      </div>
    </div>
  );
}

export { Banner, bannerVariants };
export type { BannerVariants };
